import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useFormContext } from "react-hook-form";

import { Button, Collapse, Skeleton, Toggle, Tooltip } from "components";

import { ReactComponent as CancelIcon } from "static/images/cancel.svg";
import { ReactComponent as CheckDone } from "static/images/checked.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./collapse.module.scss";

type CollapseSectionProps = {
	index?: number;
	header: string;
	recordsCount?: any;
	loading?: boolean;
	children?: any;
	className?: string;
	classNameBody?: string;
	skeletonHeight?: string;
	classNameHeader?: string;
	headerTooltipTitle?: string;
	classNameHeaderContent?: string;
	isToggled?: boolean;
	manualToggleFn?: Function;
	isSticky?: boolean;
	hasForm?: boolean;
	isDisabled?: boolean;
	icon?: React.ReactNode;
	isArrowDisabled?: boolean;
	arrowPosition?: "left" | "right";
	handleUpdateCollapseForm?: Function;
};

type RenderActionsProps = {
	tooltipTitle: string;
	buttonTitle: string;
	className?: string;
	disabled?: boolean;
	icon: React.ReactNode;
	onClick?: Function;
};

const CollapseSection = ({
	index = 0,
	header,
	recordsCount,
	loading,
	children,
	className,
	classNameBody,
	classNameHeader,
	classNameHeaderContent,
	isToggled = false,
	manualToggleFn,
	isSticky = false,
	isDisabled = false,
	hasForm = false,
	icon,
	skeletonHeight = "50px",
	isArrowDisabled,
	arrowPosition = "left",
	handleUpdateCollapseForm,
	headerTooltipTitle,
}: CollapseSectionProps) => {
	const formMethods = useFormContext();

	const [openSectionFn, setOpenSectionFn] = useState<() => void>();

	const isFormDirty = formMethods?.formState?.isDirty;
	const isFormValid = isEmpty(formMethods?.formState?.errors);
	const isEditMode = formMethods?.watch("isEditMode");

	const handleActionEditForm = (toggle, isToggled) => {
		if (hasForm && formMethods) {
			formMethods.setValue("isEditMode", !isEditMode);
			if (!isToggled) {
				toggle();
			}
		}
	};
	const setEditMode = val => {
		formMethods.setValue("isEditMode", val);
	};

	const HeaderContent = () => {
		return (
			<div
				className={clsx(
					classNameHeaderContent,
					styles.collapseHeaderContent,
				)}
			>
				{icon}
				{header}
			</div>
		);
	};

	const handleSaveUpdateValues = () => {
		handleUpdateCollapseForm?.(setEditMode);
	};

	const handleToggleClick = toggle => {
		if ((hasForm && !isFormDirty) || !hasForm) toggle?.();
	};

	useEffect(() => {
		manualToggleFn?.(index, openSectionFn);
	}, [index, manualToggleFn, openSectionFn]);

	const RenderActions = useCallback(
		({
			tooltipTitle,
			icon,
			onClick,
			buttonTitle,
			className,
			disabled = false,
		}: RenderActionsProps): JSX.Element => {
			return (
				<Tooltip
					title={tooltipTitle}
					position="bottom"
					interactive
					themeName="credit-review-queue"
				>
					<Button
						onClick={onClick}
						icon={icon}
						btnIcon
						className={className}
						variant="secondary"
						size="sm"
						title={buttonTitle}
						disabled={disabled}
					/>
				</Tooltip>
			);
		},
		[],
	);

	const CollapseHeader = ({ toggle, isToggled }) => {
		return recordsCount === 0 ? (
			<HeaderContent />
		) : (
			<div className="d-flex align-items-center">
				<Tooltip
					title={isFormDirty && headerTooltipTitle}
					position="bottom"
					themeName="credit-review-queue"
					disabled={!isFormDirty}
					interactive
				>
					<Toggle
						isToggled={isToggled}
						className={clsx(
							classNameHeader,
							isSticky && styles.sticky,
							styles.collapseHeader,
							arrowPosition === "right" &&
								styles.arrowPositionRight,
						)}
						onClick={() => handleToggleClick(toggle)}
					>
						<HeaderContent />
					</Toggle>
				</Tooltip>

				{hasForm && (
					<div className={styles.formActionContainer}>
						{isEditMode ? (
							<>
								<RenderActions
									tooltipTitle="Close Editing"
									icon={<CancelIcon />}
									onClick={() => {
										formMethods.reset();
										handleActionEditForm(toggle, isToggled);
									}}
									buttonTitle="Cancel"
									className={styles.cancelBtn}
								/>
								<RenderActions
									tooltipTitle="Save Changes"
									icon={<CheckDone />}
									onClick={handleSaveUpdateValues}
									buttonTitle="Save"
									disabled={!isFormDirty || !isFormValid}
								/>
							</>
						) : (
							<RenderActions
								tooltipTitle="Edit Parameters"
								icon={<EditIcon />}
								onClick={() => {
									handleActionEditForm(toggle, isToggled);
								}}
								buttonTitle="Edit"
							/>
						)}
					</div>
				)}
			</div>
		);
	};

	const CollapseBody = () => (
		<div className={clsx(classNameBody, styles.collapseBody)}>
			{children}
		</div>
	);

	return loading ? (
		<Skeleton height={skeletonHeight} margin="15px 0" />
	) : (
		<Collapse
			className={clsx(className, styles.collapseWrapper)}
			header={CollapseHeader}
			body={CollapseBody}
			setOpenSectionFn={setOpenSectionFn}
			bodyClassName={styles.body}
			isDisabled={isDisabled}
			defaultToggled={isToggled}
			isArrowDisabled={isArrowDisabled}
		/>
	);
};

export default CollapseSection;
